import { useState, useRef, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, UserPlus, MessageSquare, Upload, Clock, SlidersHorizontal, Check, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import type { ActivityItem } from "@shared/schema";

const PAGE_SIZE = 20;

type ActivityType = ActivityItem['type'];

const ALL_TYPES: ActivityType[] = ['document_created', 'comment_added', 'version_uploaded', 'user_joined'];

const FILTER_OPTIONS: { type: ActivityType; label: string; Icon: React.ElementType }[] = [
  { type: 'document_created', label: 'Documents created', Icon: FileText },
  { type: 'comment_added',    label: 'Comments',          Icon: MessageSquare },
  { type: 'version_uploaded', label: 'Versions uploaded', Icon: Upload },
  { type: 'user_joined',      label: 'Clients joined',    Icon: UserPlus },
];

interface ActivityFeedProps {
  organizationId: string;
  className?: string;
}

export function ActivityFeed({ organizationId, className }: ActivityFeedProps) {
  const [activeTypes, setActiveTypes] = useState<Set<ActivityType>>(new Set(ALL_TYPES));
  const [filterOpen, setFilterOpen] = useState(false);

  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const typesKey = Array.from(activeTypes).sort().join(',');

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ActivityItem[]>({
    queryKey: ["/api/organizations", organizationId, "activity", "infinite", typesKey],
    queryFn: async ({ pageParam }) => {
      const offset = (pageParam as number) ?? 0;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
        types: typesKey,
      });
      const url = `/api/organizations/${organizationId}/activity?${params}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch activity");
      return res.json();
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    initialPageParam: 0,
  });

  const toggleType = (type: ActivityType) => {
    setActiveTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size === 1) return prev;
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case 'document_created': return FileText;
      case 'user_joined':      return UserPlus;
      case 'comment_added':    return MessageSquare;
      case 'version_uploaded': return Upload;
      default:                 return Clock;
    }
  };

  const allActivities = data?.pages.flat() ?? [];
  const allTypesActive = activeTypes.size === ALL_TYPES.length;

  if (isLoading) {
    return (
      <div className={cn("space-y-1", className)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3 py-2 px-1">
            <Skeleton className="h-4 w-4 rounded mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-2.5 w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-0", className)}>
      <div className="flex items-center justify-between mb-2 px-1 gap-2">
        <span className="text-xs text-muted-foreground shrink-0">
          {allActivities.length} {allActivities.length === 1 ? 'event' : 'events'}
          {hasNextPage && '+'}
        </span>

        <div className="flex items-center gap-1.5">
          {/* Type filter dropdown */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen(v => !v)}
              className={cn(
                "flex items-center gap-1.5 text-xs px-2 py-1 rounded border transition-colors",
                filterOpen || !allTypesActive
                  ? "border-border bg-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60"
              )}
            >
              <SlidersHorizontal className="h-3 w-3" />
              <span>Filter</span>
              {!allTypesActive && (
                <span className="ml-0.5 bg-primary text-primary-foreground rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-medium">
                  {activeTypes.size}
                </span>
              )}
            </button>

            {filterOpen && (
              <div className="absolute right-0 top-full mt-1 z-20 w-52 bg-popover border border-border rounded-md shadow-md py-1">
                {FILTER_OPTIONS.map(({ type, label, Icon }) => {
                  const active = activeTypes.has(type);
                  return (
                    <button
                      key={type}
                      onClick={() => toggleType(type)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                    >
                      <span className={cn(
                        "flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-colors",
                        active ? "bg-primary border-primary" : "border-border bg-transparent"
                      )}>
                        {active && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                      </span>
                      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-foreground">{label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {allActivities.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <Clock className="h-5 w-5 text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground">No activity to show</p>
        </div>
      ) : (
        <div className="space-y-0">
          {allActivities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            return (
              <div key={activity.id} className="flex items-start gap-3 py-2 px-1 hover:bg-accent/50 rounded transition-colors">
                <Icon className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground leading-snug">
                    {activity.userName ? (
                      <>
                        <span className="font-medium">{activity.userName}</span>
                        {' '}
                        <span className="text-muted-foreground">{activity.message}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">{activity.message}</span>
                    )}
                  </p>
                  {activity.documentName && (
                    activity.documentId ? (
                      <Link
                        href={`/doc/${activity.documentId}`}
                        className="block text-xs text-muted-foreground/70 truncate mt-0.5 hover:text-muted-foreground hover:underline transition-colors"
                      >
                        {activity.documentName}
                      </Link>
                    ) : (
                      <p className="text-xs text-muted-foreground/70 truncate mt-0.5">
                        {activity.documentName}
                      </p>
                    )
                  )}
                  <span className="font-mono text-[11px] text-muted-foreground/50">
                    {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasNextPage && (
        <div className="flex justify-center pt-2 pb-1">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded px-3 py-1.5 hover:bg-accent transition-colors disabled:opacity-50"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading…</span>
              </>
            ) : (
              <span>Load more</span>
            )}
          </button>
        </div>
      )}

      {!hasNextPage && allActivities.length > 0 && (
        <p className="text-center text-[11px] text-muted-foreground/40 py-3 font-mono">
          all caught up
        </p>
      )}
    </div>
  );
}
